"use client";

import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion"; // Added for smooth transition
import Global3DBackground from "./components/Global3DBackground";
import SiteHeader from "./components/SiteHeader";
import ThreeModelShowcase from "./components/ThreeModelShowcase";
import NorrisLink from "./components/NorrisLink";
import TransitionHero from "./components/TransitionHero";
import SystemFailure from "./components/SystemFailure";
import Phase from "./components/PhaseFluidShowcase";
import BlurReveal from "./components/ScrollBlurSections";
import AsciiVideo from "./components/AsciiVideo";
import ScrambleText from "./components/ScrambleText";
import MobileSectionCaption from "./components/MobileSectionCaption";
import CestusLoader from "./components/CestusLoader"; // Ensure this component exists
import ScrollToTopButton from "./components/ScrollToTopButton";

type LandingAppProps = {
  assetBasePath?: string;
};

export default function LandingApp({}: LandingAppProps) {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="relative bg-[#02040a] text-white selection:bg-blue-500">
      
      {/* INITIAL LOADER */}
      <AnimatePresence mode="wait">
        {isLoading && (
          <CestusLoader onComplete={() => setIsLoading(false)} />
        )}
      </AnimatePresence>

      {/* MAIN CONTENT WRAPPER */}
      {/* We use a motion div to fade the content in once loading is finished */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoading ? 0 : 1 }}
        transition={{ duration: 1, ease: "easeInOut" }}
        className={isLoading ? "relative h-screen overflow-hidden" : "relative"}
      >
        <div className="fixed inset-0 z-0 pointer-events-none opacity-20" 
          style={{
            backgroundImage: `
              linear-gradient(to right, #1e293b 1px, transparent 1px),
              linear-gradient(to bottom, #1e293b 1px, transparent 1px)
            `,
            backgroundSize: '4rem 4rem',
            maskImage: 'radial-gradient(circle at center, black 40%, transparent 100%)' 
          }}
        />
        
        {/* BRANDING HEADER */}
        <SiteHeader variant="landing" />

        {/* FIXED 3D BACKGROUND */}
        <Global3DBackground />
        <MobileSectionCaption />

        {/* SCROLL CONTAINER */}
        <div id="scrolly-container" className="relative">
          
          {/* HERO */}
          {/* HERO SECTION - page.tsx */}
          <section className="h-screen flex flex-col items-center justify-center relative px-6">
            <div className="text-center z-10 pointer-events-none max-w-7xl w-full">
              
              <AnimatePresence>
                {!isLoading && (
                  <motion.div
                    layoutId="main-title" // MATCHING ID
                    transition={{ 
                      duration: 1.2, 
                      ease: [0.76, 0, 0.24, 1] // Custom "Premium" Cubic Bezier
                    }}
                    className="inline-block"
                  >
                    <h1 className="text-[25vw] md:text-[22vw] lg:text-[20vw] font-black leading-[0.8] tracking-tighter uppercase">
                      <motion.span 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-white"
                      >
                        CES
                      </motion.span>
                      <br/>
                      <motion.span 
                        initial={{ color: "#ffffff" }}
                        animate={{ color: "#2563eb" }} // Fade from white to blue
                        transition={{ delay: 0.5, duration: 0.8 }}
                        className="drop-shadow-[0_0_30px_rgba(37,99,235,0.3)]"
                      >
                        TUS
                      </motion.span>
                    </h1>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.p 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 0.5 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="mt-10 md:mt-12 font-mono text-xs md:text-sm tracking-[0.5em] text-blue-300 uppercase"
              >
                Adaptive Execution Substrate // Gen-V
              </motion.p>
            </div>
          </section>

          {/* SPECS */}
          <section className="section-inference relative min-h-[80vh] flex items-center p-8 md:p-24">
            <BlurReveal className="max-w-2xl">
              <div className="text-xs font-mono text-blue-500 mb-4 tracking-[0.4em]">/01</div>
              <div className="text-[12vw] md:text-7xl lg:text-8xl font-black mb-6 uppercase leading-[0.9] tracking-tighter">
                <ScrambleText className="block">INFERENCE</ScrambleText>
                <ScrambleText className="block">LAYER</ScrambleText>
              </div>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-xl">
                Cestus Inference Layer establishes a direct execution plane for intelligence workloads.
                Compute, memory, and model state converge into a unified runtime, enabling continuous
                inference without orchestration overhead or idle latency.
              </p>
            </BlurReveal>
          </section>

          {/* CORE */}
          <section className="section-distributed relative min-h-[80vh] flex items-center justify-end pt-32 p-8 md:p-24">
            <BlurReveal className="max-w-2xl text-right">
              <div className="text-xs font-mono text-cyan-500 mb-4 tracking-[0.4em]">/02</div>
              <div className="text-[12vw] md:text-7xl lg:text-8xl font-black mb-6 uppercase text-cyan-400 leading-[0.9] tracking-tighter">
                <ScrambleText className="block">DISTRIBUTED</ScrambleText>
                <ScrambleText className="block">EXECUTION</ScrambleText>
              </div>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-xl ml-auto">
                Cestus aggregates independent compute resources into a coherent execution domain.
                Workloads expand, contract, and relocate dynamically as demand shifts, reducing hard 
                infrastructure ceilings and centralized coordination constraints.
              </p>
            </BlurReveal>
          </section>

          {/* NEURAL BRIDGE */}
          <section className="section-memory relative min-h-[80vh] flex items-center justify-center pt-32 p-8 md:p-24">
            <BlurReveal className="max-w-2xl text-center">
              <div className="text-xs font-mono text-indigo-500 mb-4 tracking-[0.4em]">/03</div>
              <div className="text-[12vw] md:text-7xl lg:text-8xl font-black mb-6 uppercase text-indigo-400 leading-[0.9] tracking-tighter">
                <ScrambleText className="block">MEMORY</ScrambleText>
                <ScrambleText className="block">FABRIC</ScrambleText>
              </div>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
                The Memory Fabric enables high-velocity state propagation across execution nodes.
                Context, parameters, and intermediate results persist fluidly through the substrate,
                maximizing utilization for intelligence operating at extreme scale.
              </p>
            </BlurReveal>
          </section>

          {/* WIREFRAME SECTION */}
          <section id="wireframe-section" className="section-wireframe h-screen relative flex items-center justify-center">
            <div className="relative z-10 text-center pointer-events-none px-4">
              <BlurReveal>
                <h2 className="text-[12vw] md:text-8xl lg:text-9xl font-black uppercase tracking-tighter leading-[0.85] mb-6">
                  SILICON<br/>
                  <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-600 bg-clip-text text-transparent">
                    SKELETON
                  </span>
                </h2>
                <p className="max-w-2xl mx-auto text-blue-400/85 text-sm md:text-base uppercase tracking-[0.3em] font-light">
                  Architecture expressed as execution pathways - raw compute reduced to its structural essence.
                </p>
              </BlurReveal>
            </div>
          </section>

        </div>
        
        {/* REST OF PAGE */}
        <SystemFailure />
        <TransitionHero />
        <section className="relative z-10">
          <ThreeModelShowcase />
        </section>

        <Phase />

        <section className="relative z-30 h-screen w-full bg-black flex items-center justify-center">
          <AsciiVideo />
        </section>

        <div className="relative z-20 bg-[#02040a] overflow-hidden border-t border-white/5">
        <footer className="relative pt-32 pb-12 px-6 md:px-12 bg-[#02040a] border-t border-white/5 overflow-hidden">
  
        {/* 1. BACKGROUND LAYER (Subtle Data Trace) */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none font-mono text-[10px] flex gap-8 p-4">
          {!isLoading && Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1 animate-data-drift">
              {Array.from({ length: 30 }).map((_, j) => (
                <span key={j} className="text-white whitespace-nowrap">
                  {((i * 30 + j) * 999).toString(16).toUpperCase()}
                </span>
              ))}
            </div>
          ))}
        </div>

        <div className="relative z-10 max-w-[1800px] mx-auto">
          
          {/* 2. THE MONOLITH (Full Name, Color Split) */}
          <div className="w-full mb-8">
            <motion.h2 
              initial={{ y: 80, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-[26vw] font-[1000] leading-[0.75] tracking-[-0.07em] flex justify-between w-full select-none"
            >
              {/* CES - Solid White */}
              <span className="text-white">C</span>
              <span className="text-white">E</span>
              <span className="text-white">S</span>
              
              {/* TUS - Signature Blue with Glow */}
              <span className="text-blue-600 drop-shadow-[0_0_35px_rgba(37,99,235,0.4)]">T</span>
              <span className="text-blue-600 drop-shadow-[0_0_35px_rgba(37,99,235,0.4)]">U</span>
              <span className="text-blue-600 drop-shadow-[0_0_35px_rgba(37,99,235,0.4)]">S</span>
            </motion.h2>
          </div>

          {/* 3. THE DATA BAR (Integrated Links & HUD) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-white/10 pt-10 mt-4">
            
            {/* HUD Left: System State */}
            <div className="md:col-span-3 space-y-4">
              <div className="flex items-center gap-2 font-mono text-[10px] text-blue-500">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                <span>PORT_7700_ACTIVE</span>
              </div>
              <p className="font-mono text-[10px] text-white/75 uppercase leading-relaxed max-w-[200px]">
                Execution environment verified. Adaptive substrate operating at peak efficiency.
              </p>
            </div>

            {/* Center: Primary Navigation (Clean & Bold) */}
            <div className="md:col-span-6 flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24">
              <NorrisLink 
                href="https://docs.cestus.network/" 
                className="!text-5xl md:!text-7xl !font-black !tracking-tighter hover:!text-blue-500 transition-colors"
              >
                DOCS
              </NorrisLink>
              <NorrisLink 
                href="https://x.com/CestusNetwork" 
                className="!text-5xl md:!text-7xl !font-black !tracking-tighter hover:!text-blue-500 transition-colors"
              >
                TWITTER
              </NorrisLink>
            </div>

            {/* Metadata Right */}
            <div className="md:col-span-3 flex flex-col items-end justify-between text-right">
              <div className="group cursor-pointer">
                <div className="text-[10px] font-mono text-white/75 uppercase tracking-[0.4em] mb-1">Architect</div>
                <div className="text-xs font-bold text-white group-hover:text-blue-500 transition-colors uppercase tracking-tighter">
                  Cestus Network
                </div>
              </div>
              <div className="hidden md:block h-px w-24 bg-white/10 mt-6" />
            </div>

          </div>

          {/* 4. FOOTER TERMINAL BAR */}
          <div className="mt-24 pt-8 border-t border-white/5 flex justify-between items-center font-mono text-[10px] tracking-[0.5em] text-white/75 uppercase">
            <div className="flex gap-8">
              <span>V.05_CORE</span>
              <span className="hidden md:block text-white/5">|</span>
              <span className="hidden md:block text-blue-900">LATENCY: 12ms</span>
            </div>
            <span className="text-white/75">© 2026 CESTUS NETWORK</span>
          </div>
        </div>

        <style>{`
          @keyframes data-drift {
            0% { transform: translateY(0); }
            100% { transform: translateY(-30%); }
          }
          .animate-data-drift {
            animation: data-drift 60s linear infinite;
          }
        `}</style>
        </footer>
      </div>
      <ScrollToTopButton />
      </motion.div>
    </div>
  );
}
